import React, { useEffect } from "react";
import useUserInvitationStore from "../api/store";
import { dialogBox, dialogBoxButtons, dialogCont, dialogContentBox, dialogSaveButton, dialogSlider } from "../sxprops";
import { Box, Button, Dialog, DialogActions, DialogContent, Slider, Typography } from "@mui/material";
import Cropper from "react-easy-crop";

interface IImageCropDialogProps {
    open: boolean;
    onClose: () => void;
    imageUrl: string | null;
    onSave: (croppedFile: File) => void;
    originalFileName: string;

}

export const ImageCropDialog: React.FC<IImageCropDialogProps> = ({
    open,
    onClose,
    imageUrl,
    onSave,
    originalFileName
}) => {
    async function getCroppedImg(
        imageSrc: string,
        pixelCrop: { x: number; y: number; width: number; height: number }
    ): Promise<string> {
        const image = new Image();
        image.src = imageSrc;
        await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
        });

        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            throw new Error("Canvas context is null");
        }

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        // Convert to JPEG with a quality of 0.8 (adjust as needed)
        return canvas.toDataURL("image/jpeg", 0.8);
    }

    function dataURLtoFile(dataurl: string, filename: string): File {
        const arr = dataurl.split(",");
        const mimeMatch = arr[0].match(/:(.*?);/);
        if (!mimeMatch) throw new Error("Invalid data URL");
        const mime = mimeMatch[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    const { zoom, crop, croppedAreaPixels, setCroppedAreaPixels, setCrop, setZoom } = useUserInvitationStore();


    useEffect(() => {
        if (!open) {
            setZoom(1);
            setCrop({ x: 0, y: 0 });
        }
    }, [open, setCrop, setZoom]);

    const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleCancel = () => {
        onClose();
    };

    const handleSave = async () => {
        if (!imageUrl || !croppedAreaPixels) {
            onClose();
            return;
        }
        try {
            const croppedDataUrl = await getCroppedImg(imageUrl, croppedAreaPixels);
            const croppedFile = dataURLtoFile(croppedDataUrl, originalFileName);
            onSave(croppedFile);
        } catch (error) {
            onClose();
        }
    };


    return (
        <Dialog open={open} onClose={onClose} PaperProps={{
            sx: dialogCont,
            "data-testid": "image-crop-dialog",
        } as any}>
            <DialogContent sx={{ p: 0 }}>
                {imageUrl ? (
                    <Box
                        sx={dialogBox}
                    >
                        <Cropper
                            image={imageUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}


                        />
                    </Box>
                ) : (
                    <Typography>No image selected.</Typography>
                )}
                <Box sx={dialogContentBox}>
                    <Slider
                        value={zoom}
                        slotProps={{ input: { "data-testid": "image-crop-zoom-slider" } } as any}
                        sx={dialogSlider}
                        onChange={(event, value) => {
                            if (typeof value === "number") setZoom(value);
                        }}
                        min={1}
                        max={3}
                        step={0.1}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ pr: 3, pb: 2 }}>
                 <Button data-testid="image-crop-cancel-button" onClick={handleCancel}>Cancel</Button>
                    <Button data-testid="image-crop-save-button" onClick={handleSave} sx={dialogSaveButton} variant="contained" color="primary">
                        Save
                    </Button>
            </DialogActions>
        </Dialog>
    );

}